import { useState } from "react";
import { userApi } from "../api/user";
import { UpdateInterestsPayload } from "../types/user";

/**
 * Custom React hook to handle updating user interests.
 *
 * Provides a status object for loading, error, and success states,
 * a function to clear the status, and a function to send the update request.
 *
 * @returns {{
 *   status: { loading: boolean; error: string | null; success: string | null },
 *   clearStatus: () => void,
 *   send: (data: UpdateInterestsPayload) => Promise<any>
 * }}
 */
export function useInterestsUpdate() {
  // State to track the status of the update operation
  const [status, setStatus] = useState<{
    loading: boolean;
    error: string | null;
    success: string | null;
  }>({ loading: false, error: null, success: null });

  /**
   * Resets the status state to its initial values.
   */
  const clearStatus = () =>
    setStatus({ loading: false, error: null, success: null });

  /**
   * Sends the update interests request to the API.
   *
   * @param {UpdateInterestsPayload} data - The interests data to update.
   * @returns {Promise<any>} The API response.
   * @throws Will throw an error if the update fails.
   */
  const send = async (data: UpdateInterestsPayload) => {
    setStatus({ loading: true, error: null, success: null });
    const fd = new FormData();
    // Append disciplines, industries, and keywords to the FormData if present
    if (data.disciplines) fd.append("disciplines", data.disciplines);
    if (data.industries) fd.append("industries", data.industries);
    if (data.keywords) fd.append("keywords", data.keywords);
    try {
      // Call the API to update interests
      const response = await userApi.updateInterests(fd);
      setStatus({ loading: false, error: null, success: "Updated!" });
      return response;
    } catch (err: any) {
      // Set error status if the API call fails
      setStatus({
        loading: false,
        error: err?.message || "Failed to update interests",
        success: null,
      });
      throw err;
    }
  };

  // Return the status, clearStatus, and send function for use in components
  return { status, clearStatus, send };
}
